import React from 'react';

// ─────────────────────────────────────────────────────────────────
// Placeholder blog post store
// One JS array. Not a CMS. Will migrate to a real platform later.
// Body can be JSX so we can format paragraphs and links inline.
// ─────────────────────────────────────────────────────────────────

export const POSTS = [
  {
    slug: 'introducing-groupardy',
    title: 'Introducing Groupardy',
    date: '2026-05-26',
    author: 'JT Burgess',
    authorRole: 'Founder',
    excerpt:
      "Trivia night should feel like a game show, not a Google Form. So we built one — with an AI host that actually reads the clues, and boards written for the room you're standing in.",
    readMinutes: 4,
    body: (
      <>
        <p>
          If you've ever tried to run trivia for a group, you know the pattern.
          One person volunteers to host. They read clues off their phone in a
          voice that is doing its best. Someone asks them to repeat the question.
          They repeat the question. Now the energy is gone, and three of your
          friends are on Instagram.
        </p>
        <p>
          Trivia is one of the few activities that genuinely scales — two friends
          on a couch, twelve coworkers in a conference room, a hundred strangers
          in a bar — and it almost always gets undersold by the tools we use to
          run it. The format is right there: a board, some categories, a host
          with timing. The pieces just don't fit together when one of the
          players has to be the show.
        </p>
        <h2>What Groupardy is</h2>
        <p>
          Groupardy is a Jeopardy-style trivia game show for groups. The board
          runs on a TV, laptop, or projector. Players join from their phones.
          And the host — the part that usually breaks — is an AI that reads
          every clue out loud, judges open-ended answers, reacts to wagers, and
          crowns a winner without ever needing a turn skipped.
        </p>
        <p>
          You give it a topic — your group, your office, your wedding, "early
          2000s pop music" — and Groupardy generates a fifty-clue game across
          two rounds plus a Final Jeopardy, with categories written for the
          room you're standing in. The clues aren't recycled from a trivia
          database you've already seen at three different bars.
        </p>
        <h2>What makes it different</h2>
        <p>Two things, mostly.</p>
        <p>
          <strong>The boards are written for you.</strong> Most trivia products
          are a fixed catalog of questions. Groupardy writes a new board every
          time, tuned to the audience you describe. "Onboarding for the data
          team" is a different board than "my mom's 70th birthday" is a
          different board than "Tuesday night at the bar". The format is the
          same; the content is yours.
        </p>
        <p>
          <strong>The host is a real host.</strong> Captions, pacing, judging
          open-ended answers, narrating a wager mid-round — those are show
          mechanics, and they're the difference between a quiz and a game
          night. We spent a lot of time on the host. It pauses on Daily
          Doubles. It knows that "Constantinople" and "Istanbul" are both
          fine. It will not, under any circumstances, reward sarcasm with
          points.
        </p>
        <h2>What's next</h2>
        <p>
          Today: a working game, up to 100 players, free for a limited time
          while we're still in early access. From here we're focused on four
          things: better host tools (smarter narration, more reactions, more
          theatrical timing), more host personalities to pick from, a smoother
          game-creation flow so the board ends up more dialed-in to your
          specific group, and tighter question tuning so the difficulty
          actually lands where it should — easy for the casual round, brutal
          for the trivia diehards.
        </p>
        <p>
          We'll use this space to talk about what we're building and why,
          share game-night patterns that work (and a few that don't), and post
          the occasional category that was too good not to publish. No
          newsletter sign-up. No "thought leadership". Just notes from the
          room.
        </p>
        <p>Thanks for being here. Now go host something.</p>
      </>
    ),
  },
];

export const getPostBySlug = (slug) => POSTS.find((p) => p.slug === slug);

export const getAllPosts = () =>
  [...POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));
